import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FloorplanMaster } from '../../interfaces/floorplan.interface';
import { FloorplanService } from '../../services/floorplan.service';
import { I18nService } from '../../../../services/i18n.service';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { FileUploadService } from '../../../../services/file-upload.service';
import { environment } from '../../../../services/global-environment';
import { DepartmentService } from '../../../../services/department.service';
import { Department } from '../../../../models/department.model';
import { AuthService } from '../../../../services/auth.service';
import { AssistantUrlService } from '../../../../services/assistant-url.service';
import { PermissionBaseComponent } from '../../../../conponents/base/permission-base.component';
import { ComponentPermissionService } from '../../../../services/component-permission.service';

@Component({
  selector: 'app-floorplan-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslatePipe
  ],
  templateUrl: './floorplan-list.component.html',
  styleUrls: ['./floorplan-list.component.scss']
})
export class FloorplanListComponent extends PermissionBaseComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // 資料狀態
  floorplans: FloorplanMaster[] = [];
  loading = false;
  
  // 篩選和搜尋
  searchTerm = '';
  selectedBuilding = '';
  selectedFloor = '';
  selectedDepartment = ''; // 部門篩選

  // 篩選選項
  buildings: string[] = [];
  floors: string[] = [];
  departments: Department[] = []; // 部門清單
  departmentsLoaded = false; // 部門載入狀態

  // 顯示模式
  viewMode: 'grid' | 'list' = 'list';

  // 助手編號
  private assistantNo = '';

  // 對話框狀態
  showAddDialog = false;
  showDropdownMenu: string | null = null;
  
  // 新增平面圖對話框相關
  newFloorplan = {
    name: '',
    description: '',
    floor_level: '',
    building_name: '',
    departmentId: '', // 所屬部門 ID
    departmentName: '', // 所屬部門名稱
    scale_ratio: null as number | null,
    scale_unit: 'cm' as 'cm' | 'm',
    real_width: null as number | null,
    real_height: null as number | null
  };
  selectedFile: File | null = null;
  isUploading = false;
  
  // 通知狀態
  notification = {
    show: false,
    message: '',
    type: 'info' as 'info' | 'success' | 'error'
  };

  // 儲存建立的 Blob URL 以便清理
  private createdBlobUrls: string[] = [];

  constructor(
    private floorplanService: FloorplanService,
    private i18nService: I18nService,
    private fileUploadService: FileUploadService,
    private departmentService: DepartmentService,
    private authService: AuthService,
    private assistantUrlService: AssistantUrlService,
    protected override permissionService: ComponentPermissionService
  ) {
    super(permissionService);
  }

  async ngOnInit() {
    console.log('🏗️ 平面圖列表元件初始化');
    this.assistantNo = this.assistantUrlService.getAssistantIdFromUrl();

    // 載入權限
    await this.loadPermissions('floorplanListComponent');

    this.loadFloorplans();
    this.loadDepartments();
  }

  ngOnDestroy() {
    // 清理建立的 Blob URL
    this.createdBlobUrls.forEach(url => {
      URL.revokeObjectURL(url);
    });
    this.createdBlobUrls = [];
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * 載入部門清單（依權限過濾）
   */
  loadDepartments(): void {
    console.log('[FloorplanList] 載入部門資料...');

    // 取得使用者資訊
    const userInfo = this.authService.getUserInfo();

    // 建立查詢選項
    const options: any = { enabledOnly: true };

    // 如果有權限限制（非 'all'），加入權限參數
    if (this.componentPermission?.dataScope && this.componentPermission.dataScope !== 'all') {
      options.dataScope = this.componentPermission.dataScope;
      options.userid = userInfo.userid;
      options.assistantNo = this.assistantNo;
      console.log('[FloorplanList] 使用權限過濾載入部門:', options);
    } else {
      console.log('[FloorplanList] 載入所有部門（無權限限制）');
    }

    this.departmentService.getDepartmentList(options)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (departments) => {
          console.log('[FloorplanList] 部門資料載入成功:', departments);
          this.departments = departments;
          this.departmentsLoaded = true;
        },
        error: (error) => {
          console.error('[FloorplanList] 部門資料載入失敗:', error);
          this.departments = [];
          this.departmentsLoaded = true;
        }
      });
  }

  /**
   * 格式化部門名稱（加入階層縮排）
   */
  formatDepartmentName(department: Department): string {
    const indent = '　'.repeat(department.level); // 使用全形空格
    return `${indent}${department.name}`;
  }

  /**
   * 部門選擇變更處理
   */
  onDepartmentChange(departmentId: string): void {
    const selectedDept = this.departments.find(dept => dept.id === departmentId);
    this.newFloorplan.departmentName = selectedDept ? selectedDept.name : '';
  }

  /**
   * 載入平面圖列表
   */
  loadFloorplans() {
    this.loading = true;

    // 取得使用者資訊
    const userInfo = this.authService.getUserInfo();

    const params: any = {
      building_name: this.selectedBuilding || undefined,
      floor_level: this.selectedFloor || undefined,
      page: 1,
      limit: 50,
      search: this.searchTerm || undefined
    };

    // 如果有權限限制（非 'all'），加入權限參數給後端過濾
    if (this.componentPermission?.dataScope && this.componentPermission.dataScope !== 'all') {
      params.dataScope = this.componentPermission.dataScope;
      params.userid = userInfo.userid;
      params.assistantNo = this.assistantNo;
      console.log('[FloorplanList] 使用權限過濾載入平面圖:', {
        dataScope: params.dataScope,
        userid: params.userid,
        assistantNo: params.assistantNo
      });
    } else {
      console.log('[FloorplanList] 載入所有平面圖（無權限限制）');
    }

    this.floorplanService.getFloorplanList(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.floorplans = response.data.floorplans;
            this.updateFilterOptions();
            console.log('✅ 平面圖列表載入成功:', this.floorplans.length, '個平面圖');
          } else {
            this.showError(this.i18nService.translate('floorplan.list.load_failed'));
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('❌ 載入平面圖列表失敗:', error);
          this.showError(this.i18nService.translate('floorplan.list.load_failed'));
          this.loading = false;
        }
      });
  }

  /**
   * 更新篩選選項
   */
  private updateFilterOptions() {
    this.buildings = Array.from(new Set(
      this.floorplans
        .map(fp => fp.building_name)
        .filter(name => name)
    )) as string[];
    
    this.floors = Array.from(new Set(
      this.floorplans.map(fp => fp.floor_level)
    )).sort();
  }

  /**
   * 搜尋和篩選
   */
  get filteredFloorplans(): FloorplanMaster[] {
    return this.floorplans.filter(fp => {
      const matchesSearch = !this.searchTerm ||
        fp.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        fp.description?.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesBuilding = !this.selectedBuilding ||
        fp.building_name === this.selectedBuilding;

      const matchesFloor = !this.selectedFloor ||
        fp.floor_level === this.selectedFloor;

      const matchesDepartment = !this.selectedDepartment ||
        fp.departmentId === this.selectedDepartment;

      return matchesSearch && matchesBuilding && matchesFloor && matchesDepartment;
    });
  }

  /**
   * 重新整理
   */
  refresh() {
    this.loadFloorplans();
    this.showNotification('已重新整理', 'success');
  }

  /**
   * 清除篩選
   */
  clearFilters() {
    this.searchTerm = '';
    this.selectedBuilding = '';
    this.selectedFloor = '';
    this.selectedDepartment = '';
  }

  /**
   * 開啟平面圖編輯器 - 新分頁開啟
   */
  openEditor(floorplan?: FloorplanMaster) {
    if (floorplan) {
      console.log('🎨 開啟平面圖編輯器（新分頁）:', floorplan.name);
      // 建立編輯器的完整 URL
      const editorUrl = `/floorplan/editor/${floorplan.id}`;
      // 以新分頁開啟
      window.open(editorUrl, '_blank');
    } else {
      console.log('📝 開啟新增平面圖對話框');
      this.openAddFloorplanDialog();
    }
  }

  /**
   * 開啟新增平面圖對話框
   */
  openAddFloorplanDialog() {
    // 重置表單
    this.resetForm();
    // 開啟對話框
    this.showAddDialog = true;
  }
  
  /**
   * 關閉新增平面圖對話框
   */
  closeAddFloorplanDialog() {
    this.showAddDialog = false;
    this.resetForm();
  }
  
  /**
   * 確認建立平面圖 - 導向編輯器，不直接儲存
   */
  confirmCreateFloorplan() {
    console.log('✅ 用戶確認建立平面圖');
    
    // 驗證必要欄位
    if (!this.selectedFile) {
      this.showNotification('請選擇平面圖檔案', 'error');
      return;
    }

    if (!this.newFloorplan.name.trim()) {
      this.showNotification('請輸入平面圖名稱', 'error');
      return;
    }

    // 建立新平面圖物件（前端暫存，尚未儲存到資料庫）
    this.createNewFloorplanLocally();
  }

  /**
   * 重置新增表單
   */
  private resetForm() {
    this.newFloorplan = {
      name: '',
      description: '',
      floor_level: '',
      building_name: '',
      departmentId: '',
      departmentName: '',
      scale_ratio: null,
      scale_unit: 'cm',
      real_width: null,
      real_height: null
    };
    this.selectedFile = null;
    this.isUploading = false;
  }

  /**
   * 檔案選擇處理
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // 檢查檔案類型
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.showNotification('請選擇圖片檔案 (JPG, PNG, GIF, BMP) 或 PDF 檔案', 'error');
        return;
      }

      // 檢查檔案大小 (限制 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        this.showNotification(this.i18nService.translate('file_upload.file_size_exceeded', { maxSize: 50 }), 'error');
        return;
      }

      this.selectedFile = file;
      console.log('📁 檔案選擇:', file.name, file.type, this.formatFileSize(file.size));

      // 如果平面圖名稱為空，使用檔案名稱（去除副檔名）
      if (!this.newFloorplan.name) {
        this.newFloorplan.name = file.name.split('.')[0];
      }
    }
  }

  /**
   * 建立新平面圖物件（前端暫存）
   */
  async createNewFloorplanLocally() {
    try {
      // 先顯示上傳中狀態
      this.isUploading = true;
      this.showNotification('上傳檔案中，請稍候...', 'info');
      
      // 上傳檔案到 FileUploadService
      const uploadResult = await this.uploadFileToService(this.selectedFile!);
      if (!uploadResult.success || !uploadResult.url) {
        throw new Error(uploadResult.error || '檔案上傳失敗');
      }
      
      // 取得圖片尺寸
      const imageData = await this.getImageDimensions(this.selectedFile!);
      
      // 生成新的平面圖 ID
      const newId = `floorplan_${Date.now()}`;
      
      // 建立前端暫存的平面圖物件
      const newFloorplan: FloorplanMaster = {
        id: newId,
        created_by: this.authService.getUserInfo().userid,
        name: this.newFloorplan.name.trim(),
        description: this.newFloorplan.description?.trim() || undefined,
        floor_level: this.newFloorplan.floor_level.trim(),
        building_name: this.newFloorplan.building_name?.trim() || undefined,
        departmentId: this.newFloorplan.departmentId || undefined,
        departmentName: this.newFloorplan.departmentName || undefined,
        file_url: uploadResult.url!, // 使用上傳後的實際 URL (已在上面檢查過不為空)
        file_type: this.selectedFile!.type.startsWith('image/') ? 'image' : 'pdf',
        file_size: this.selectedFile!.size,
        scale_ratio: this.newFloorplan.scale_ratio || undefined,
        scale_unit: this.newFloorplan.scale_unit,
        width_pixels: imageData.width,
        height_pixels: imageData.height,
        real_width: this.newFloorplan.real_width || undefined,
        real_height: this.newFloorplan.real_height || undefined,
        is_active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('✅ 平面圖物件建立成功（已上傳檔案）:', newFloorplan);
      
      this.isUploading = false;
      
      // 關閉對話框
      this.closeAddFloorplanDialog();
      
      // 將新建立的平面圖資料暫時存到 sessionStorage，供新分頁使用
      sessionStorage.setItem('newFloorplanData', JSON.stringify({
        newFloorplan: newFloorplan,
        isNew: true
      }));
      
      // 以新分頁開啟編輯器
      const editorUrl = `/floorplan/editor`;
      window.open(editorUrl, '_blank');
      
    } catch (error) {
      console.error('❌ 建立平面圖物件失敗:', error);
      this.isUploading = false;
      this.showNotification('建立平面圖失敗', 'error');
    }
  }

  /**
   * 儲存平面圖到資料庫（由編輯器呼叫）
   * 這個方法應該從編輯器元件中呼叫，用於將前端暫存的平面圖儲存到資料庫
   */
  saveFloorplanToDatabase(floorplanData: Omit<FloorplanMaster, 'id' | 'companyid' | 'createdAt' | 'updatedAt'>): Promise<FloorplanMaster | null> {
    return new Promise((resolve, reject) => {
      this.floorplanService.createFloorplan(floorplanData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success && response.data) {
              console.log('✅ 平面圖儲存成功:', response.data);
              // 重新載入列表
              this.loadFloorplans();
              resolve(response.data);
            } else {
              console.error('❌ 儲存失敗:', response.error);
              reject(new Error(response.error?.message || '儲存失敗'));
            }
          },
          error: (error) => {
            console.error('❌ API 呼叫失敗:', error);
            reject(error);
          }
        });
    });
  }

  /**
   * 上傳檔案到 FileUploadService
   */
  private uploadFileToService(file: File): Promise<{success: boolean, url?: string, error?: string}> {
    return new Promise((resolve) => {
      this.fileUploadService.uploadFile(file)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (event: any) => {
            if (event.type === 'success' && event.response) {
              console.log('✅ 檔案上傳成功:', event.response);
              console.log('🔍 檢查 data 結構:', event.response.data);
              
              if (event.response.success && event.response.data) {
                // 檢查不同可能的 URL 欄位位置
                const url = event.response.data.url || 
                           event.response.data.viewUrl || 
                           event.response.data.downloadUrl ||
                           event.response.data.fileUrl;
                
                if (url) {
                  resolve({
                    success: true,
                    url: url
                  });
                } else {
                  console.error('❌ 無法找到 URL，data 內容:', event.response.data);
                  resolve({
                    success: false,
                    error: '無法取得檔案 URL'
                  });
                }
              } else {
                resolve({
                  success: false,
                  error: event.response.error?.message || '上傳失敗'
                });
              }
            } else if (event.type === 'progress') {
              // 處理上傳進度（可選）
              console.log('📈 上傳進度:', event.progress + '%');
            }
          },
          error: (error) => {
            console.error('❌ 檔案上傳 API 錯誤:', error);
            resolve({
              success: false,
              error: error.message || '上傳過程發生錯誤'
            });
          }
        });
    });
  }

  /**
   * 取得圖片尺寸
   */
  private getImageDimensions(file: File): Promise<{width: number, height: number}> {
    return new Promise((resolve, reject) => {
      if (file.type === 'application/pdf') {
        // PDF 檔案預設尺寸
        resolve({ width: 1920, height: 1080 });
        return;
      }

      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        reject(new Error('無法讀取圖片尺寸'));
      };
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * 複製平面圖
   */
  duplicateFloorplan(floorplan: FloorplanMaster, event: Event) {
    event.stopPropagation();
    // TODO: 實作複製功能
    this.showNotification(`複製「${floorplan.name}」功能開發中`, 'info');
    this.hideDropdownMenu();
  }

  /**
   * 刪除平面圖
   */
  deleteFloorplan(floorplan: FloorplanMaster, event: Event) {
    event.stopPropagation();
    // TODO: 實作刪除功能
    this.showNotification(`刪除「${floorplan.name}」功能開發中`, 'info');
    this.hideDropdownMenu();
  }

  /**
   * 格式化檔案大小
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 格式化日期
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * 取得樓層排序順序
   */
  getFloorOrder(floor: string): number {
    if (floor.includes('B')) {
      return -parseInt(floor.replace('B', ''));
    } else if (floor.includes('F')) {
      return parseInt(floor.replace('F', ''));
    } else {
      return parseInt(floor) || 0;
    }
  }

  /**
   * 顯示通知訊息
   */
  showNotification(message: string, type: 'info' | 'success' | 'error' = 'info') {
    this.notification = { show: true, message, type };
    setTimeout(() => {
      this.notification.show = false;
    }, type === 'error' ? 5000 : 3000);
  }
  
  /**
   * 顯示錯誤訊息
   */
  private showError(message: string) {
    this.showNotification(message, 'error');
  }
  
  /**
   * 顯示/隱藏下拉選單
   */
  toggleDropdownMenu(id: string, event: Event) {
    event.stopPropagation();
    this.showDropdownMenu = this.showDropdownMenu === id ? null : id;
  }
  
  /**
   * 隱藏下拉選單
   */
  hideDropdownMenu() {
    this.showDropdownMenu = null;
  }
  
  /**
   * 關閉通知
   */
  closeNotification() {
    this.notification.show = false;
  }
}